import * as THREE from 'three';
import { FikoWasmBridge } from '../fiko-performance/FikoWasmBridge';
import { BrepBody, BrepVertex, BrepEdge, BrepFace, BrepLoop, BrepShell, BrepSolid, MeshAnalysisResult, BrepCurve, BrepSurface } from './FikoBrep';

/**
 * FikoBrepWasmAnalyzer - WASM-powered BREP Topology Analyzer
 *
 * FikoWasmBridge kullanarak grup bazlı BREP analizi yapar
 * - Indexed/merged geometry desteği
 * - Grup attribute'larına göre face/edge/vertex extraction
 * - WASM accelerated topology calculations
 */
export class FikoBrepWasmAnalyzer {
    private wasmBridge: FikoWasmBridge;
    private tolerance: number;

    constructor(tolerance: number = 1e-6) {
        this.wasmBridge = FikoWasmBridge.getInstance();
        this.tolerance = tolerance;
    }

    /**
     * Ana BREP analiz metodu - grup bazlı
     */
    public async analyzeMesh(
        meshGeom: THREE.BufferGeometry,
        options?: {
            tolerance?: number;
            useGroups?: boolean;
            recognizeSurfaces?: boolean;
        }
    ): Promise<MeshAnalysisResult> {
        this.tolerance = options?.tolerance ?? this.tolerance;
        const useGroups = options?.useGroups ?? true;

        const brep: BrepBody = {
            vertices: new Map(),
            edges: new Map(),
            loops: new Map(),
            faces: new Map(),
            shells: new Map(),
            solids: new Map(),
            tolerance: this.tolerance,
            boundingBox: new THREE.Box3(),
            stats: {
                vertexCount: 0,
                edgeCount: 0,
                faceCount: 0,
                shellCount: 0,
                solidCount: 0,
                totalVolume: 0,
                totalArea: 0,
            }
        };

        // 1. Grup bilgisini çıkar (meshGeom'da grup attribute varsa)
        const gruplar = this.extractGroups(meshGeom);

        // 2. Vertex extraction (indexed geometry optimize)
        this.extractVertices(meshGeom, brep);

        // 3. Edge extraction (grup bazlı, WASM accelerated)
        await this.extractEdges(meshGeom, brep, gruplar, useGroups);

        // 4. Face extraction (grup bazlı)
        await this.extractFaces(meshGeom, brep, gruplar, useGroups);

        // 5. Loop extraction
        this.extractLoops(brep);

        // 6. Shell extraction (grup bazlı)
        this.extractShells(brep, gruplar);

        // 7. Solid extraction
        this.extractSolids(brep);

        // 8. Yüzey tanıma (opsiyonel)
        if (options?.recognizeSurfaces ?? true) {
            await this.recognizeSurfaces(brep, meshGeom);
        }

        // 9. Topolojik analiz
        const topology = this.analyzeTopology(brep);

        // 10. Geometrik özellik çıkarımı
        const geometry = this.analyzeGeometry(brep);

        // 11. Feature sınıflandırma
        const features = this.classifyFeatures(brep);

        // 12. Bounding box hesapla
        meshGeom.computeBoundingBox();
        if (meshGeom.boundingBox) {
            brep.boundingBox.copy(meshGeom.boundingBox);
        }

        return {
            brep,
            features,
            topology,
            geometry
        };
    }

    // ========== GRUP EXTRACTION ==========

    /**
     * Geometry'deki grup attribute'unu çıkarır
     * grup attribute format: [strt, syi, grp] per vertex
     */
    private extractGroups(meshGeom: THREE.BufferGeometry): Map<number, Set<number>> {
        const grupAttribute = meshGeom.attributes['grup'];
        const grupMap = new Map<number, Set<number>>(); // grp -> face indices

        if (!grupAttribute) {
            return grupMap;
        }

        // Triangle indekslerini dolaş
        const triangleCount = meshGeom.index
            ? meshGeom.index.count / 3
            : meshGeom.attributes['position'].count / 3;

        for (let i = 0; i < triangleCount; i++) {
            // İlk vertex'in grup bilgisini al (triangle'ın tamamı aynı grupta)
            const vertexIdx = meshGeom.index ? meshGeom.index.getX(i * 3) : i * 3;
            const grp = Math.floor(grupAttribute.getZ(vertexIdx));

            if (!grupMap.has(grp)) {
                grupMap.set(grp, new Set());
            }
            grupMap.get(grp)!.add(i);
        }

        return grupMap;
    }

    // ========== VERTEX EXTRACTION ==========

    /**
     * Indexed geometry için optimize vertex extraction
     */
    private extractVertices(meshGeom: THREE.BufferGeometry, brep: BrepBody): void {
        const positions = meshGeom.attributes['position'];
        if (!positions) return;

        // Indexed geometry - her position unique
        for (let i = 0; i < positions.count; i++) {
            const pos = new THREE.Vector3(
                positions.getX(i),
                positions.getY(i),
                positions.getZ(i)
            );

            const id = `V${i}`;
            brep.vertices.set(id, {
                id,
                position: pos,
                tolerance: this.tolerance,
                edges: []
            });
        }

        brep.stats.vertexCount = brep.vertices.size;
    }

    // ========== EDGE EXTRACTION (WASM ACCELERATED) ==========

    /**
     * Edge extraction - WASM ile ultra-fast
     */
    private async extractEdges(
        meshGeom: THREE.BufferGeometry,
        brep: BrepBody,
        gruplar: Map<number, Set<number>>,
        useGroups: boolean
    ): Promise<void> {
        // WASM kullanmayı dene
        await this.wasmBridge.ensureReady();
        const wasmModule = this.wasmBridge.getWasmModule();

        if (wasmModule && wasmModule.BrepAnalyzer) {
            await this.extractEdgesWasm(meshGeom, brep, gruplar);
            return;
        }

        await this.extractEdgesJS(meshGeom, brep, gruplar, useGroups);
    }

    /**
     * WASM-powered edge extraction
     */
    private async extractEdgesWasm(
        meshGeom: THREE.BufferGeometry,
        brep: BrepBody,
        gruplar: Map<number, Set<number>>
    ): Promise<void> {
        const wasmModule = this.wasmBridge.getWasmModule();
        if (!wasmModule) return;

        // Geometry data hazırla
        const positions = meshGeom.attributes['position'];
        const index = meshGeom.index;
        const grupAttr = meshGeom.attributes['grup'];

        if (!positions || !index) return;

        // Float32Array -> regular array (WASM için)
        const posArray = new Float32Array(positions.array);
        const idxArray = new Uint32Array(index.array);

        // Grup array (opsiyonel)
        let grupArray: Uint32Array | undefined;
        if (grupAttr) {
            const grpData = new Uint32Array(positions.count);
            for (let i = 0; i < positions.count; i++) {
                grpData[i] = Math.floor(grupAttr.getZ(i));
            }
            grupArray = grpData;
        }

        try {
            // WASM analyzer çağır
            const analyzer = new wasmModule.BrepAnalyzer(this.tolerance);
            const result = analyzer.analyzeMesh(posArray, idxArray, grupArray || null);

            // Sonuçları brep'e aktar
            brep.stats.vertexCount = result.getVertexCount();
            brep.stats.edgeCount = result.getEdgeCount();
            brep.stats.faceCount = result.getFaceCount();
            brep.stats.shellCount = result.getShellCount();

            // Edge data al
            const edgeData = result.getEdgeData(); // [v0, v1, faceCount, angle, length, convex, ...]
            let edgeId = 0;

            for (let i = 0; i < edgeData.length; i += 6) {
                const v0Id = `V${Math.floor(edgeData[i])}`;
                const v1Id = `V${Math.floor(edgeData[i + 1])}`;
                const faceCount = Math.floor(edgeData[i + 2]);
                const angle = edgeData[i + 3];
                const length = edgeData[i + 4];
                const convex = edgeData[i + 5] > 0.5;

                const v0 = brep.vertices.get(v0Id);
                const v1 = brep.vertices.get(v1Id);

                if (!v0 || !v1) continue;

                const curve: BrepCurve = {
                    type: 'line',
                    start: v0.position.clone(),
                    end: v1.position.clone(),
                    length: length
                };

                const edge: BrepEdge = {
                    id: `E${edgeId++}`,
                    startVertex: v0Id,
                    endVertex: v1Id,
                    curve,
                    faces: [], // Face ID'leri sonra eklenir
                    convex,
                    angle,
                    length
                };

                brep.edges.set(edge.id, edge);

                // Vertex'e edge ekle
                v0.edges.push(edge.id);
                v1.edges.push(edge.id);
            }

        } catch (error) {
            throw error;
        }
    }

    /**
     * JavaScript fallback edge extraction
     */
    private async extractEdgesJS(
        meshGeom: THREE.BufferGeometry,
        brep: BrepBody,
        gruplar: Map<number, Set<number>>,
        useGroups: boolean
    ): Promise<void> {
        const positions = meshGeom.attributes['position'];
        const index = meshGeom.index;

        if (!positions) return;

        const edgeMap = new Map<string, BrepEdge>(); // edge hash -> edge
        let edgeId = 0;

        // Triangle'ları dolaş
        const triangleCount = index ? index.count / 3 : positions.count / 3;

        for (let i = 0; i < triangleCount; i++) {
            // Vertex indeksleri
            const i0 = index ? index.getX(i * 3 + 0) : i * 3 + 0;
            const i1 = index ? index.getX(i * 3 + 1) : i * 3 + 1;
            const i2 = index ? index.getX(i * 3 + 2) : i * 3 + 2;

            const faceId = `F${i}`;

            // 3 edge oluştur
            this.addEdge(edgeMap, brep, i0, i1, faceId, () => `E${edgeId++}`);
            this.addEdge(edgeMap, brep, i1, i2, faceId, () => `E${edgeId++}`);
            this.addEdge(edgeMap, brep, i2, i0, faceId, () => `E${edgeId++}`);
        }

        // Edge'leri brep'e ekle
        edgeMap.forEach(edge => brep.edges.set(edge.id, edge));
        brep.stats.edgeCount = brep.edges.size;

        // Edge açılarını hesapla (komşu face'ler için)
        await this.calculateEdgeAngles(brep, meshGeom);
    }

    /**
     * Tek bir edge ekler (duplicate kontrolü ile)
     */
    private addEdge(
        edgeMap: Map<string, BrepEdge>,
        brep: BrepBody,
        v0Idx: number,
        v1Idx: number,
        faceId: string,
        idGenerator: () => string
    ): void {
        const v0Id = `V${v0Idx}`;
        const v1Id = `V${v1Idx}`;

        const v0 = brep.vertices.get(v0Id);
        const v1 = brep.vertices.get(v1Id);

        if (!v0 || !v1) return;

        // Edge hash (sırasız - v0-v1 ve v1-v0 aynı)
        const edgeHash = this.edgeHash(v0Id, v1Id);

        if (edgeMap.has(edgeHash)) {
            // Mevcut edge'e face ekle
            const edge = edgeMap.get(edgeHash)!;
            edge.faces.push(faceId);
        } else {
            // Yeni edge oluştur
            const curve: BrepCurve = {
                type: 'line',
                start: v0.position.clone(),
                end: v1.position.clone(),
                length: v0.position.distanceTo(v1.position)
            };

            const edge: BrepEdge = {
                id: idGenerator(),
                startVertex: v0Id,
                endVertex: v1Id,
                curve,
                faces: [faceId],
                convex: true, // sonra hesaplanacak
                angle: 0,
                length: curve.length
            };

            edgeMap.set(edgeHash, edge);

            // Vertex'e edge ekle
            v0.edges.push(edge.id);
            v1.edges.push(edge.id);
        }
    }

    /**
     * Edge açılarını hesapla (komşu face normalleri ile)
     */
    private async calculateEdgeAngles(brep: BrepBody, meshGeom: THREE.BufferGeometry): Promise<void> {
        const positions = meshGeom.attributes['position'];
        const index = meshGeom.index;

        // Face normal'lerini hesapla
        const faceNormals = new Map<string, THREE.Vector3>();

        const triangleCount = index ? index.count / 3 : positions.count / 3;
        for (let i = 0; i < triangleCount; i++) {
            const i0 = index ? index.getX(i * 3 + 0) : i * 3 + 0;
            const i1 = index ? index.getX(i * 3 + 1) : i * 3 + 1;
            const i2 = index ? index.getX(i * 3 + 2) : i * 3 + 2;

            const v0 = new THREE.Vector3(positions.getX(i0), positions.getY(i0), positions.getZ(i0));
            const v1 = new THREE.Vector3(positions.getX(i1), positions.getY(i1), positions.getZ(i1));
            const v2 = new THREE.Vector3(positions.getX(i2), positions.getY(i2), positions.getZ(i2));

            const edge1 = v1.clone().sub(v0);
            const edge2 = v2.clone().sub(v0);
            const normal = edge1.cross(edge2).normalize();

            faceNormals.set(`F${i}`, normal);
        }

        // Edge açılarını hesapla
        brep.edges.forEach(edge => {
            if (edge.faces.length === 2) {
                const n1 = faceNormals.get(edge.faces[0]);
                const n2 = faceNormals.get(edge.faces[1]);

                if (n1 && n2) {
                    // Açı hesapla (derece cinsinden)
                    const dot = THREE.MathUtils.clamp(n1.dot(n2), -1, 1);
                    const angleRad = Math.acos(dot);
                    edge.angle = THREE.MathUtils.radToDeg(angleRad);

                    // Convex/concave kontrolü
                    edge.convex = edge.angle < 180;
                }
            }
        });
    }

    // ========== FACE EXTRACTION (GRUP BAZLI) ==========

    /**
     * Face extraction - grup bazlı, triangle'ları face'lere grupla
     */
    private async extractFaces(
        meshGeom: THREE.BufferGeometry,
        brep: BrepBody,
        gruplar: Map<number, Set<number>>,
        useGroups: boolean
    ): Promise<void> {
        const positions = meshGeom.attributes['position'];
        const index = meshGeom.index;
        const colorAttr = meshGeom.attributes['color'];

        if (!positions) return;

        // Her triangle'ı başlangıçta ayrı face olarak ekle
        const triangleCount = index ? index.count / 3 : positions.count / 3;

        for (let i = 0; i < triangleCount; i++) {
            const i0 = index ? index.getX(i * 3 + 0) : i * 3 + 0;
            const i1 = index ? index.getX(i * 3 + 1) : i * 3 + 1;
            const i2 = index ? index.getX(i * 3 + 2) : i * 3 + 2;

            const v0 = new THREE.Vector3(positions.getX(i0), positions.getY(i0), positions.getZ(i0));
            const v1 = new THREE.Vector3(positions.getX(i1), positions.getY(i1), positions.getZ(i1));
            const v2 = new THREE.Vector3(positions.getX(i2), positions.getY(i2), positions.getZ(i2));

            // Normal hesapla
            const edge1 = v1.clone().sub(v0);
            const edge2 = v2.clone().sub(v0);
            const normal = edge1.cross(edge2).normalize();

            // Alan hesapla
            const area = edge1.cross(edge2).length() / 2;

            // Centroid hesapla
            const centroid = v0.clone().add(v1).add(v2).divideScalar(3);

            // Renk al (varsa)
            let color: THREE.Color | undefined;
            if (colorAttr) {
                const r = colorAttr.getX(i0);
                const g = colorAttr.getY(i0);
                const b = colorAttr.getZ(i0);
                color = new THREE.Color(r, g, b);
            }

            // Face oluştur (başlangıçta freeform)
            const face: BrepFace = {
                id: `F${i}`,
                surface: {
                    type: 'freeform',
                    triangles: [new THREE.Triangle(v0, v1, v2)]
                },
                loops: [],
                edges: [],
                normal: normal,
                area: area,
                centroid: centroid,
                color: color
            };

            brep.faces.set(face.id, face);
        }

        brep.stats.faceCount = brep.faces.size;
    }

    // ========== LOOP EXTRACTION ==========

    /**
     * Face boundary loop'larını çıkar
     */
    private extractLoops(brep: BrepBody): void {
        // Her face için boundary edge'leri bul
        brep.faces.forEach(face => {
            const boundaryEdges: string[] = [];

            // Face'e ait edge'leri bul (sadece bu face'e ait olan veya boundary)
            brep.edges.forEach(edge => {
                if (edge.faces.includes(face.id)) {
                    // Eğer edge sadece 1 face'e aitse, boundary'dir
                    if (edge.faces.length === 1) {
                        boundaryEdges.push(edge.id);
                    }
                }
            });

            if (boundaryEdges.length > 0) {
                const loop: BrepLoop = {
                    id: `L${face.id}`,
                    edges: boundaryEdges,
                    isOuter: true,
                    vertices: []
                };

                brep.loops.set(loop.id, loop);
                face.loops.push(loop);
            }
        });

    }

    // ========== SHELL EXTRACTION (GRUP BAZLI) ==========

    /**
     * Bağlı face'ları shell olarak grupla
     */
    private extractShells(brep: BrepBody, gruplar: Map<number, Set<number>>): void {
        let shellId = 0;

        // Grup bazlı shell oluştur
        gruplar.forEach((faceIndices, grp) => {
            const faceIds = Array.from(faceIndices).map(i => `F${i}`);

            const shell: BrepShell = {
                id: `S${shellId++}`,
                faces: faceIds,
                isClosed: this.isShellClosed(faceIds, brep),
                boundingBox: new THREE.Box3()
            };

            // Volume hesapla (kapalıysa)
            if (shell.isClosed) {
                shell.volume = this.calculateShellVolume(shell, brep);
            }

            brep.shells.set(shell.id, shell);
        });

        // Grup yoksa tüm face'leri tek shell'e koy
        if (gruplar.size === 0) {
            const allFaceIds = Array.from(brep.faces.keys());

            const shell: BrepShell = {
                id: `S0`,
                faces: allFaceIds,
                isClosed: this.isShellClosed(allFaceIds, brep),
                boundingBox: new THREE.Box3()
            };

            if (shell.isClosed) {
                shell.volume = this.calculateShellVolume(shell, brep);
            }

            brep.shells.set(shell.id, shell);
        }

        brep.stats.shellCount = brep.shells.size;
    }

    /**
     * Shell kapalı mı kontrolü (watertight)
     */
    private isShellClosed(faceIds: string[], brep: BrepBody): boolean {
        // Tüm edge'lerin tam 2 face'e bağlı olması gerekir
        const shellEdges = new Set<string>();

        faceIds.forEach(faceId => {
            brep.edges.forEach(edge => {
                if (edge.faces.includes(faceId)) {
                    shellEdges.add(edge.id);
                }
            });
        });

        for (const edgeId of shellEdges) {
            const edge = brep.edges.get(edgeId)!;
            const shellFaceCount = edge.faces.filter(f => faceIds.includes(f)).length;

            if (shellFaceCount !== 2) {
                return false; // Boundary edge bulundu
            }
        }

        return true;
    }

    /**
     * Shell hacmi hesapla (signed volume)
     */
    private calculateShellVolume(shell: BrepShell, brep: BrepBody): number {
        let volume = 0;

        shell.faces.forEach(faceId => {
            const face = brep.faces.get(faceId);
            if (!face || face.surface.type !== 'freeform') return;

            face.surface.triangles.forEach(tri => {
                // Signed volume (tetrahedron)
                const v0 = tri.a;
                const v1 = tri.b;
                const v2 = tri.c;

                volume += v0.dot(v1.clone().cross(v2)) / 6;
            });
        });

        return Math.abs(volume);
    }

    // ========== SOLID EXTRACTION ==========

    /**
     * Kapalı shell'leri solid olarak işaretle
     */
    private extractSolids(brep: BrepBody): void {
        let solidId = 0;

        brep.shells.forEach(shell => {
            if (shell.isClosed && shell.volume && shell.volume > 0) {
                const solid: BrepSolid = {
                    id: `SO${solidId++}`,
                    outerShell: shell.id,
                    voids: [],
                    volume: shell.volume,
                    centerOfMass: new THREE.Vector3(), // TODO: hesapla
                    boundingBox: shell.boundingBox
                };

                brep.solids.set(solid.id, solid);
                brep.stats.totalVolume += solid.volume;
            }
        });

        brep.stats.solidCount = brep.solids.size;
    }

    // ========== ADVANCED SURFACE RECOGNITION SYSTEM ==========

    /**
     * Mükemmel yüzey tanıma sistemi - her yüzeyi doğru şekilde tanır
     */
    private async recognizeSurfaces(brep: BrepBody, meshGeom: THREE.BufferGeometry): Promise<void> {

        // İlk olarak WASM kullanmayı dene
        await this.wasmBridge.ensureReady();
        const wasmModule = this.wasmBridge.getWasmModule();

        let useWasm = false;
        if (wasmModule && wasmModule.SurfaceRecognizer) {
            useWasm = true;
        }

        // Yüzey tanıma sonuçları
        const results = {
            planes: 0,
            cylinders: 0,
            spheres: 0,
            cones: 0,
            tori: 0,
            freeform: 0
        };

        // Her face'i analiz et
        for (const [faceId, face] of brep.faces) {
            try {
                // Face noktalarını topla
                const facePoints = this.extractFacePoints(face, meshGeom);
                if (facePoints.length < 3) continue;

                // WASM veya JavaScript ile yüzey tanıma
                let surfaceType: BrepSurface['type'] = 'freeform';
                let surfaceData: any = {};

                if (useWasm) {
                    const wasmResult = await this.recognizeSurfaceWASM(facePoints, wasmModule);
                    surfaceType = wasmResult.type;
                    surfaceData = wasmResult.data;
                } else {
                    const jsResult = await this.recognizeSurfaceJS(facePoints);
                    surfaceType = jsResult.type;
                    surfaceData = jsResult.data;
                }

                // Face'i güncelle
                switch (surfaceType) {
                    case 'plane':
                        face.surface = {
                            type: 'plane',
                            origin: surfaceData.origin,
                            normal: surfaceData.normal,
                            uAxis: surfaceData.uAxis,
                            vAxis: surfaceData.vAxis
                        };
                        results.planes++;
                        break;

                    case 'cylinder':
                        face.surface = {
                            type: 'cylinder',
                            axis: surfaceData.axis,
                            center: surfaceData.center,
                            radius: surfaceData.radius,
                            height: surfaceData.height
                        };
                        results.cylinders++;
                        break;

                    case 'sphere':
                        face.surface = {
                            type: 'sphere',
                            center: surfaceData.center,
                            radius: surfaceData.radius
                        };
                        results.spheres++;
                        break;

                    case 'cone':
                        face.surface = {
                            type: 'cone',
                            apex: surfaceData.apex,
                            axis: surfaceData.axis,
                            radius: surfaceData.radius,
                            angle: surfaceData.angle
                        };
                        results.cones++;
                        break;

                    case 'torus':
                        face.surface = {
                            type: 'torus',
                            center: surfaceData.center,
                            axis: surfaceData.axis,
                            majorRadius: surfaceData.majorRadius,
                            minorRadius: surfaceData.minorRadius
                        };
                        results.tori++;
                        break;

                    default:
                        // Freeform olarak bırak
                        results.freeform++;
                        break;
                }

            } catch (error) {
                results.freeform++;
            }
        }


        // Post-processing: komşu yüzeyleri birleştir
        await this.postProcessSurfaces(brep);
    }

    /**
     * Face noktalarını çıkar
     */
    private extractFacePoints(face: BrepFace, meshGeom: THREE.BufferGeometry): THREE.Vector3[] {
        const points: THREE.Vector3[] = [];

        if (face.surface.type === 'freeform' && face.surface.triangles) {
            // Triangle'lardan noktaları topla
            face.surface.triangles.forEach(tri => {
                points.push(tri.a.clone(), tri.b.clone(), tri.c.clone());
            });
        } else {
            // Face'in vertex'lerini doğrudan al
            const positions = meshGeom.attributes['position'];
            const index = meshGeom.index;

            // Face ID'sinden triangle indeksini bul
            const faceIdx = parseInt(face.id.substring(1)); // F123 -> 123

            const i0 = index ? index.getX(faceIdx * 3) : faceIdx * 3;
            const i1 = index ? index.getX(faceIdx * 3 + 1) : faceIdx * 3 + 1;
            const i2 = index ? index.getX(faceIdx * 3 + 2) : faceIdx * 3 + 2;

            points.push(
                new THREE.Vector3(positions.getX(i0), positions.getY(i0), positions.getZ(i0)),
                new THREE.Vector3(positions.getX(i1), positions.getY(i1), positions.getZ(i1)),
                new THREE.Vector3(positions.getX(i2), positions.getY(i2), positions.getZ(i2))
            );
        }

        return points;
    }

    /**
     * WASM ile yüzey tanıma
     */
    private async recognizeSurfaceWASM(points: THREE.Vector3[], wasmModule: any): Promise<{type: BrepSurface['type'], data: any}> {
        // Point array'ini Float32Array'e dönüştür
        const pointArray = new Float32Array(points.length * 3);
        points.forEach((p, i) => {
            pointArray[i * 3] = p.x;
            pointArray[i * 3 + 1] = p.y;
            pointArray[i * 3 + 2] = p.z;
        });

        const recognizer = new wasmModule.SurfaceRecognizer(this.tolerance);
        const surfaceInfo = recognizer.recognizeSurface(pointArray);
        const surfaceType = surfaceInfo.getSurfaceType();

        const typeMap = ['plane', 'cylinder', 'sphere', 'cone', 'torus', 'freeform'];
        const type = typeMap[surfaceType] || 'freeform';

        let data: any = {};

        switch (type) {
            case 'plane':
                const origin = surfaceInfo.getPlaneOrigin();
                const normal = surfaceInfo.getPlaneNormal();
                data = {
                    origin: new THREE.Vector3(origin[0], origin[1], origin[2]),
                    normal: new THREE.Vector3(normal[0], normal[1], normal[2]),
                    uAxis: this.computeUAxis(new THREE.Vector3(normal[0], normal[1], normal[2])),
                    vAxis: this.computeVAxis(new THREE.Vector3(normal[0], normal[1], normal[2])),
                    size: this.computeFaceSize(points)
                };
                break;

            case 'cylinder':
                const cylAxis = surfaceInfo.getCylinderAxis();
                const cylCenter = surfaceInfo.getCylinderCenter();
                const cylRadius = surfaceInfo.getCylinderRadius();
                data = {
                    axis: new THREE.Vector3(cylAxis[0], cylAxis[1], cylAxis[2]),
                    center: new THREE.Vector3(cylCenter[0], cylCenter[1], cylCenter[2]),
                    radius: cylRadius,
                    height: this.computeCylinderHeight(points, new THREE.Vector3(cylAxis[0], cylAxis[1], cylAxis[2]))
                };
                break;

            case 'sphere':
                const sphCenter = surfaceInfo.getSphereCenter();
                const sphRadius = surfaceInfo.getSphereRadius();
                data = {
                    center: new THREE.Vector3(sphCenter[0], sphCenter[1], sphCenter[2]),
                    radius: sphRadius
                };
                break;
        }

        return { type: type as BrepSurface['type'], data };
    }

    /**
     * JavaScript ile gelişmiş yüzey tanıma (fallback)
     */
    private async recognizeSurfaceJS(points: THREE.Vector3[]): Promise<{type: BrepSurface['type'], data: any}> {
        // 1. Plane kontrolü - en basit ve en yaygın
        const planeResult = this.checkIfPlane(points);
        if (planeResult.isPlane) {
            return {
                type: 'plane',
                data: planeResult
            };
        }

        // 2. Sphere kontrolü
        const sphereResult = this.checkIfSphere(points);
        if (sphereResult.isSphere) {
            return {
                type: 'sphere',
                data: sphereResult
            };
        }

        // 3. Cylinder kontrolü
        const cylinderResult = this.checkIfCylinder(points);
        if (cylinderResult.isCylinder) {
            return {
                type: 'cylinder',
                data: cylinderResult
            };
        }

        // 4. Cone kontrolü
        const coneResult = this.checkIfCone(points);
        if (coneResult.isCone) {
            return {
                type: 'cone',
                data: coneResult
            };
        }

        // 5. Torus kontrolü (daha karmaşık)
        const torusResult = this.checkIfTorus(points);
        if (torusResult.isTorus) {
            return {
                type: 'torus',
                data: torusResult
            };
        }

        // 6. Freeform - hiçbiri uymuyorsa
        return {
            type: 'freeform',
            data: { triangles: this.triangulatePoints(points) }
        };
    }

    /**
     * Plane kontrolü - en hassas yöntem
     */
    private checkIfPlane(points: THREE.Vector3[]): any {
        if (points.length < 3) return { isPlane: false };

        // En küçük kareler yöntemi ile plane fit
        const plane = this.fitPlane(points);
        if (!plane) return { isPlane: false };

        // Tüm noktaların plandaki sapmasını kontrol et
        let maxDeviation = 0;
        let totalDeviation = 0;

        for (const point of points) {
            const deviation = Math.abs(plane.normal.dot(point) - plane.d);
            maxDeviation = Math.max(maxDeviation, deviation);
            totalDeviation += deviation;
        }

        const avgDeviation = totalDeviation / points.length;

        // Tolerans kontrolü - daha esnek
        const adaptiveTolerance = Math.max(this.tolerance * 10, this.tolerance * 10 * (1 + Math.log10(points.length) * 0.5));
        const adaptiveAvgTolerance = Math.max(this.tolerance * 5, this.tolerance * 5 * (1 + Math.log10(points.length) * 0.3));

        if (maxDeviation < adaptiveTolerance && avgDeviation < adaptiveAvgTolerance) {
            // Normal'i standardize et (yönü önemli)
            if (plane.normal.z < 0) plane.normal.negate();

            return {
                isPlane: true,
                origin: plane.origin,
                normal: plane.normal,
                uAxis: this.computeUAxis(plane.normal),
                vAxis: this.computeVAxis(plane.normal),
                size: this.computeFaceSize(points),
                maxDeviation,
                avgDeviation
            };
        }

        return { isPlane: false };
    }

    /**
     * Sphere kontrolü
     */
    private checkIfSphere(points: THREE.Vector3[]): any {
        if (points.length < 4) return { isSphere: false };

        // Center'ı tahmin et (bounding box merkezinden başla)
        const center = this.estimateSphereCenter(points);
        const radius = this.estimateSphereRadius(points, center);

        // Tüm noktaların sapmasını kontrol et
        let maxDeviation = 0;
        let totalDeviation = 0;

        for (const point of points) {
            const dist = point.distanceTo(center);
            const deviation = Math.abs(dist - radius);
            maxDeviation = Math.max(maxDeviation, deviation);
            totalDeviation += deviation;
        }

        const avgDeviation = totalDeviation / points.length;

        const sphereTolerance = Math.max(this.tolerance * 20, this.tolerance * 20 * (1 + Math.log10(points.length) * 0.5));
        const sphereAvgTolerance = Math.max(this.tolerance * 10, this.tolerance * 10 * (1 + Math.log10(points.length) * 0.3));

        if (maxDeviation < sphereTolerance && avgDeviation < sphereAvgTolerance) {
            return {
                isSphere: true,
                center,
                radius,
                maxDeviation,
                avgDeviation
            };
        }

        return { isSphere: false };
    }

    /**
     * Cylinder kontrolü
     */
    private checkIfCylinder(points: THREE.Vector3[]): any {
        if (points.length < 6) return { isCylinder: false };

        // PCA ile ana eksenleri bul
        const pca = this.computePCA(points);
        const axis = pca.vectors[0]; // Ana bileşen

        // Noktaları eksen etrafında döndür
        const projected = this.projectToPlane(points, axis);

        // 2D'de circle fit et
        const circle = this.fitCircle2D(projected);

        if (!circle) return { isCylinder: false };

        // Radius ve sapmaları kontrol et
        let maxDeviation = 0;
        let totalDeviation = 0;

        for (let i = 0; i < points.length; i++) {
            const dist2D = Math.sqrt(
                Math.pow(projected[i].x - circle.center.x, 2) +
                Math.pow(projected[i].y - circle.center.y, 2)
            );
            const deviation = Math.abs(dist2D - circle.radius);
            maxDeviation = Math.max(maxDeviation, deviation);
            totalDeviation += deviation;
        }

        const avgDeviation = totalDeviation / points.length;

        const cylinderTolerance = Math.max(this.tolerance * 15, this.tolerance * 15 * (1 + Math.log10(points.length) * 0.5));
        const cylinderAvgTolerance = Math.max(this.tolerance * 8, this.tolerance * 8 * (1 + Math.log10(points.length) * 0.3));

        if (maxDeviation < cylinderTolerance && avgDeviation < cylinderAvgTolerance) {
            return {
                isCylinder: true,
                axis,
                center: circle.center,
                radius: circle.radius,
                height: this.computeCylinderHeight(points, axis),
                maxDeviation,
                avgDeviation
            };
        }

        return { isCylinder: false };
    }

    /**
     * Cone kontrolü
     */
    private checkIfCone(points: THREE.Vector3[]): any {
        if (points.length < 6) return { isCone: false };

        // Apex ve base'i bul
        const apex = this.findConeApex(points);
        if (!apex) return { isCone: false };

        const baseCircle = this.findConeBase(points, apex);
        if (!baseCircle) return { isCone: false };

        // Tüm noktaların cone üzerinde olup olmadığını kontrol et
        let maxDeviation = 0;
        let totalDeviation = 0;

        for (const point of points) {
            const deviation = this.distanceToConeSurface(point, apex, baseCircle.center, baseCircle.radius);
            maxDeviation = Math.max(maxDeviation, deviation);
            totalDeviation += deviation;
        }

        const avgDeviation = totalDeviation / points.length;

        if (maxDeviation < this.tolerance * 20 && avgDeviation < this.tolerance * 10) {
            const axis = baseCircle.center.clone().sub(apex).normalize();
            return {
                isCone: true,
                apex,
                axis,
                radius: baseCircle.radius,
                angle: Math.atan2(baseCircle.radius, apex.distanceTo(baseCircle.center)),
                maxDeviation,
                avgDeviation
            };
        }

        return { isCone: false };
    }

    /**
     * Torus kontrolü (en karmaşık)
     */
    private checkIfTorus(points: THREE.Vector3[]): any {
        if (points.length < 12) return { isTorus: false };

        // Torusun merkez eksenini bul
        const axis = this.findTorusAxis(points);
        if (!axis) return { isTorus: false };

        // Major radius'ı tahmin et
        const centerCircle = this.fitCenterCircle(points, axis);
        if (!centerCircle) return { isTorus: false };

        const majorRadius = centerCircle.radius;
        const center = centerCircle.center;

        // Minor radius'ı hesapla
        const distances = points.map(p => {
            const proj = p.clone().sub(center).projectOnPlane(axis);
            return p.distanceTo(proj);
        });

        const minorRadius = distances.reduce((a, b) => a + b, 0) / distances.length;

        // Sapmaları kontrol et
        let maxDeviation = 0;
        let totalDeviation = 0;

        for (const point of points) {
            const deviation = this.distanceToTorusSurface(point, center, axis, majorRadius, minorRadius);
            maxDeviation = Math.max(maxDeviation, deviation);
            totalDeviation += deviation;
        }

        const avgDeviation = totalDeviation / points.length;

        if (maxDeviation < this.tolerance * 25 && avgDeviation < this.tolerance * 15) {
            return {
                isTorus: true,
                center,
                axis,
                majorRadius,
                minorRadius,
                maxDeviation,
                avgDeviation
            };
        }

        return { isTorus: false };
    }

    // ========== MATEMATİKSEL YARDIMCI FONKSİYONLAR ==========

    /**
     * Plane fit (en küçük kareler)
     */
    private fitPlane(points: THREE.Vector3[]): { normal: THREE.Vector3, origin: THREE.Vector3, d: number } | null {
        if (points.length < 3) return null;

        // Covariance matrix hesapla
        const center = new THREE.Vector3();
        points.forEach(p => center.add(p));
        center.divideScalar(points.length);

        let xx = 0, xy = 0, xz = 0, yy = 0, yz = 0, zz = 0;
        points.forEach(p => {
            const dx = p.x - center.x;
            const dy = p.y - center.y;
            const dz = p.z - center.z;
            xx += dx * dx;
            xy += dx * dy;
            xz += dx * dz;
            yy += dy * dy;
            yz += dy * dz;
            zz += dz * dz;
        });

        // En küçük özdeğerli özvektörü bul (normal)
        const detX = yy * zz - yz * yz;
        const detY = xz * yz - xy * zz;
        const detZ = xy * yz - xz * yy;

        let maxDet = Math.abs(detX);
        let normal = new THREE.Vector3(detX, detY, detZ);

        if (Math.abs(detY) > maxDet) {
            maxDet = Math.abs(detY);
            normal = new THREE.Vector3(detX, detY, detZ);
        }
        if (Math.abs(detZ) > maxDet) {
            normal = new THREE.Vector3(detX, detY, detZ);
        }

        normal.normalize();

        return {
            normal,
            origin: center,
            d: normal.dot(center)
        };
    }

    /**
     * PCA (Principal Component Analysis)
     */
    private computePCA(points: THREE.Vector3[]): { vectors: THREE.Vector3[], values: number[] } {
        // Center of mass
        const center = new THREE.Vector3();
        points.forEach(p => center.add(p));
        center.divideScalar(points.length);

        // Covariance matrix
        const cov = [
            [0, 0, 0],
            [0, 0, 0],
            [0, 0, 0]
        ];

        points.forEach(p => {
            const dx = p.x - center.x;
            const dy = p.y - center.y;
            const dz = p.z - center.z;

            cov[0][0] += dx * dx;
            cov[0][1] += dx * dy;
            cov[0][2] += dx * dz;
            cov[1][0] += dy * dx;
            cov[1][1] += dy * dy;
            cov[1][2] += dy * dz;
            cov[2][0] += dz * dx;
            cov[2][1] += dz * dy;
            cov[2][2] += dz * dz;
        });

        // Özdeğerler ve özvektörler (basit Jacobi yöntemi)
        const values = [0, 0, 0];
        const vectors = [
            new THREE.Vector3(1, 0, 0),
            new THREE.Vector3(0, 1, 0),
            new THREE.Vector3(0, 0, 1)
        ];

        // Basitlik için sadece en büyük özvektörü hesapla (power iteration)
        let v = new THREE.Vector3(1, 1, 1).normalize();
        for (let i = 0; i < 10; i++) {
            const newV = new THREE.Vector3(
                cov[0][0] * v.x + cov[0][1] * v.y + cov[0][2] * v.z,
                cov[1][0] * v.x + cov[1][1] * v.y + cov[1][2] * v.z,
                cov[2][0] * v.x + cov[2][1] * v.y + cov[2][2] * v.z
            );
            v = newV.normalize();
        }

        vectors[0] = v;
        values[0] = v.x * (cov[0][0] * v.x + cov[0][1] * v.y + cov[0][2] * v.z) +
                     v.y * (cov[1][0] * v.x + cov[1][1] * v.y + cov[1][2] * v.z) +
                     v.z * (cov[2][0] * v.x + cov[2][1] * v.y + cov[2][2] * v.z);

        return { vectors, values };
    }

    /**
     * 2D Circle fit (Taubin yöntemi)
     */
    private fitCircle2D(points: THREE.Vector3[]): { center: THREE.Vector2, radius: number } | null {
        if (points.length < 3) return null;

        // Taubin algoritması
        let xx = 0, yy = 0, xy = 0;
        let x = 0, y = 0;
        let x2y2 = 0;
        let xxx = 0, yyy = 0;
        let xxy = 0, xyy = 0;

        for (const p of points) {
            xx += p.x * p.x;
            yy += p.y * p.y;
            xy += p.x * p.y;
            x += p.x;
            y += p.y;
            x2y2 += p.x * p.x + p.y * p.y;
            xxx += p.x * p.x * p.x;
            yyy += p.y * p.y * p.y;
            xxy += p.x * p.x * p.y;
            xyy += p.x * p.y * p.y;
        }

        const n = points.length;
        const A = [[xxx + xyy, xxy, x], [xxy, xyy + yyy, y], [x, y, n]];
        const B = [-x2y2 / 2, -x2y2 / 2, -n];

        // Linear system çöz (determinant)
        const detA = A[0][0] * (A[1][1] * A[2][2] - A[1][2] * A[2][1]) -
                     A[0][1] * (A[1][0] * A[2][2] - A[1][2] * A[2][0]) +
                     A[0][2] * (A[1][0] * A[2][1] - A[1][1] * A[2][0]);

        if (Math.abs(detA) < 1e-10) return null;

        const centerX = (B[0] * (A[1][1] * A[2][2] - A[1][2] * A[2][1]) -
                       A[0][1] * (B[1] * A[2][2] - A[1][2] * B[2]) +
                       A[0][2] * (B[1] * A[2][1] - A[1][1] * B[2])) / detA;

        const centerY = (A[0][0] * (B[1] * A[2][2] - A[1][2] * B[2]) -
                       B[0] * (A[1][0] * A[2][2] - A[1][2] * A[2][0]) +
                       A[0][2] * (A[1][0] * B[2] - B[1] * A[2][0])) / detA;

        const center = new THREE.Vector2(centerX, centerY);

        let radius = 0;
        for (const p of points) {
            radius += Math.sqrt(Math.pow(p.x - center.x, 2) + Math.pow(p.y - center.y, 2));
        }
        radius /= points.length;

        return { center, radius };
    }

    // ========== Diğer yardımcı fonksiyonlar ==========

    private estimateSphereCenter(points: THREE.Vector3[]): THREE.Vector3 {
        // Bounding box merkezinden başla
        const box = new THREE.Box3();
        points.forEach(p => box.expandByPoint(p));
        return box.getCenter(new THREE.Vector3());
    }

    private estimateSphereRadius(points: THREE.Vector3[], center: THREE.Vector3): number {
        let totalRadius = 0;
        points.forEach(p => totalRadius += p.distanceTo(center));
        return totalRadius / points.length;
    }

    private projectToPlane(points: THREE.Vector3[], normal: THREE.Vector3): THREE.Vector3[] {
        return points.map(p => p.clone().projectOnPlane(normal));
    }

    private computeCylinderHeight(points: THREE.Vector3[], axis: THREE.Vector3): number {
        // Noktaları eksen üzerine projektize et ve min/max bul
        let min = Infinity, max = -Infinity;

        for (const p of points) {
            const proj = p.dot(axis);
            min = Math.min(min, proj);
            max = Math.max(max, proj);
        }

        return max - min;
    }

    private computeFaceSize(points: THREE.Vector3[]): { width: number, height: number } {
        // 2D bounding box
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;

        for (const p of points) {
            minX = Math.min(minX, p.x);
            maxX = Math.max(maxX, p.x);
            minY = Math.min(minY, p.y);
            maxY = Math.max(maxY, p.y);
        }

        return {
            width: maxX - minX,
            height: maxY - minY
        };
    }

    private findConeApex(points: THREE.Vector3[]): THREE.Vector3 | null {
        // En dıştaki noktaları bul
        const center = new THREE.Vector3();
        points.forEach(p => center.add(p));
        center.divideScalar(points.length);

        // Uzaklık sırasına göre sırala
        const sorted = [...points].sort((a, b) => b.distanceTo(center) - a.distanceTo(center));

        // En uzak noktaların kesişimini kontrol et
        for (let i = 0; i < Math.min(5, sorted.length - 2); i++) {
            for (let j = i + 1; j < Math.min(6, sorted.length - 1); j++) {
                for (let k = j + 1; k < Math.min(7, sorted.length); k++) {
                    // 3 noktadan geçen düzlemleri bul
                    const plane1 = new THREE.Plane().setFromCoplanarPoints(sorted[i], sorted[j], sorted[k]);

                    // Kaç nokta bu düzleme yakın?
                    let count = 0;
                    for (const p of points) {
                        if (plane1.distanceToPoint(p) < this.tolerance * 10) {
                            count++;
                        }
                    }

                    // Eğer %70'den fazlası aynı düzlemdeyse apex olabilir
                    if (count > points.length * 0.7) {
                        return sorted[i].clone(); // Apex olarak dön
                    }
                }
            }
        }

        return null;
    }

    private findConeBase(points: THREE.Vector3[], apex: THREE.Vector3): { center: THREE.Vector3, radius: number } | null {
        // Apex'ten en uzak noktaları bul
        const farthest = [...points]
            .sort((a, b) => b.distanceTo(apex) - a.distanceTo(apex))
            .slice(0, 10);

        // Bu noktaları 2D'ye projektize et ve circle fit et
        const axis = apex.clone().normalize();
        const projected = farthest.map(p => p.clone().projectOnPlane(axis));

        const circle = this.fitCircle2D(projected);
        if (!circle) return null;

        // 3D center'ı geri dönüştür
        const center3D = new THREE.Vector3(circle.center.x, circle.center.y, 0)
            .applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), axis));

        return {
            center: center3D,
            radius: circle.radius
        };
    }

    private distanceToConeSurface(point: THREE.Vector3, apex: THREE.Vector3, baseCenter: THREE.Vector3, baseRadius: number): number {
        const axis = baseCenter.clone().sub(apex).normalize();
        const height = apex.distanceTo(baseCenter);

        // Noktanın eksen üzerindeki pozisyonu
        const projOnAxis = apex.clone().add(axis.clone().multiplyScalar(point.clone().sub(apex).dot(axis)));

        // O pozisyondaki koni yarıçapı
        const distFromApex = projOnAxis.distanceTo(apex);
        const expectedRadius = (distFromApex / height) * baseRadius;

        // Gerçek yarıçap
        const actualRadius = point.distanceTo(projOnAxis);

        return Math.abs(actualRadius - expectedRadius);
    }

    private findTorusAxis(points: THREE.Vector3[]): THREE.Vector3 | null {
        // Torusun simetri eksenini bul
        const pca = this.computePCA(points);

        // İkinci en büyük özdeğer, torus eksenine olmalı
        if (pca.values[1] > pca.values[2] * 2) {
            return pca.vectors[1].normalize();
        }

        return null;
    }

    private fitCenterCircle(points: THREE.Vector3[], axis: THREE.Vector3): { center: THREE.Vector3, radius: number } | null {
        // Noktaları eksen diklemine projektize et
        const projected = points.map(p => p.clone().projectOnPlane(axis));

        // 2D'de circle fit
        const circle = this.fitCircle2D(projected);
        if (!circle) return null;

        // 3D center'ı geri dönüştür
        const center3D = new THREE.Vector3(circle.center.x, circle.center.y, 0)
            .applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), axis));

        return {
            center: center3D,
            radius: circle.radius
        };
    }

    private distanceToTorusSurface(point: THREE.Vector3, center: THREE.Vector3, axis: THREE.Vector3, majorRadius: number, minorRadius: number): number {
        // Noktayı torus koordinat sistemine dönüştür
        const relPoint = point.clone().sub(center);
        const projOnAxis = relPoint.dot(axis);

        // Eksen diklemine projektize et
        const projOnPlane = relPoint.clone().sub(axis.clone().multiplyScalar(projOnAxis));

        // Major circle'dan uzaklık
        const distFromMajor = Math.abs(projOnPlane.length() - majorRadius);

        // Total uzaklık (minor radius'dan sapma)
        return Math.sqrt(distFromMajor * distFromMajor + projOnAxis * projOnAxis) - minorRadius;
    }

    private triangulatePoints(points: THREE.Vector3[]): THREE.Triangle[] {
        // Delaunay triangulation (basit implementasyon)
        const triangles: THREE.Triangle[] = [];

        // Basit ear clipping algoritması
        if (points.length >= 3) {
            for (let i = 1; i < points.length - 1; i++) {
                triangles.push(new THREE.Triangle(points[0], points[i], points[i + 1]));
            }
        }

        return triangles;
    }

    /**
     * Post-processing: komşu yüzeyleri birleştir
     */
    private async postProcessSurfaces(brep: BrepBody): Promise<void> {

        // Benzer yüzeyleri grupla
        const surfaceGroups = new Map<string, string[]>(); // surface type -> face IDs

        brep.faces.forEach((face, faceId) => {
            const type = face.surface.type;
            if (!surfaceGroups.has(type)) {
                surfaceGroups.set(type, []);
            }
            surfaceGroups.get(type)!.push(faceId);
        });

        // Her grup için birleştirme kontrolü
        surfaceGroups.forEach((faceIds, type) => {
            if (type === 'plane' && faceIds.length > 1) {
                // Paralel düzlemleri birleştir
                this.mergeParallelPlanes(brep, faceIds);
            } else if (type === 'cylinder' && faceIds.length > 1) {
                // Aynı eksendeki silindirleri birleştir
                this.mergeCoaxialCylinders(brep, faceIds);
            }
        });
    }

    private mergeParallelPlanes(brep: BrepBody, faceIds: string[]): void {
        // Paralel düzlemleri birleştir

        const planeGroups = new Map<string, string[]>(); // normal hash -> face IDs
        const TOLERANCE_ANGLE = Math.PI / 180 * 5; // 5 derece
        const TOLERANCE_DISTANCE = this.tolerance * 10;

        faceIds.forEach(faceId => {
            const face = brep.faces.get(faceId);
            if (!face || face.surface.type !== 'plane') return;

            const normal = face.surface.normal;

            // Normal için hash key oluştur
            const hash = `${Math.round(normal.x / TOLERANCE_ANGLE)},${Math.round(normal.y / TOLERANCE_ANGLE)},${Math.round(normal.z / TOLERANCE_ANGLE)}`;

            if (!planeGroups.has(hash)) {
                planeGroups.set(hash, []);
            }
            planeGroups.get(hash)!.push(faceId);
        });

        // Her grup için tek düzlem oluştur
        let mergedCount = 0;
        planeGroups.forEach(group => {
            if (group.length > 1) {
                const mergedPlane = this.mergePlaneGroup(brep, group);
                if (mergedPlane) {
                    mergedCount++;
                    // Gruplanmış face'leri sil
                    group.forEach(id => brep.faces.delete(id));
                    // Birleştirilmiş yüzeyi ekle
                    brep.faces.set(mergedPlane.id, mergedPlane);
                }
            }
        });

    }

    private mergeCoaxialCylinders(brep: BrepBody, faceIds: string[]): void {
        // Ko-aks silindirleri birleştir

        const cylinderGroups = new Map<string, string[]>(); // axis hash -> face IDs
        const TOLERANCE_ANGLE = Math.PI / 180 * 5; // 5 derece
        const TOLERANCE_AXIS = this.tolerance * 5;
        const TOLERANCE_RADIUS = this.tolerance * 10;

        faceIds.forEach(faceId => {
            const face = brep.faces.get(faceId);
            if (!face || face.surface.type !== 'cylinder') return;

            const axis = face.surface.axis.normalize();

            // Eksen için hash key oluştur
            const hash = `${Math.round(axis.x / TOLERANCE_ANGLE)},${Math.round(axis.y / TOLERANCE_ANGLE)},${Math.round(axis.z / TOLERANCE_ANGLE)}`;

            if (!cylinderGroups.has(hash)) {
                cylinderGroups.set(hash, []);
            }
            cylinderGroups.get(hash)!.push(faceId);
        });

        // Her grup için tek silindir oluştur
        let mergedCount = 0;
        cylinderGroups.forEach(group => {
            if (group.length > 1) {
                const mergedCylinder = this.mergeCylinderGroup(brep, group);
                if (mergedCylinder) {
                    mergedCount++;
                    // Gruplanmış face'leri sil
                    group.forEach(id => brep.faces.delete(id));
                    // Birleştirilmiş yüzeyi ekle
                    brep.faces.set(mergedCylinder.id, mergedCylinder);
                }
            }
        });

    }

    /**
     * Bir düzlem grubunu tek düzlem olarak birleştirir
     */
    private mergePlaneGroup(brep: BrepBody, faceIds: string[]): BrepFace | null {
        if (faceIds.length === 0) return null;

        // Tüm face'lerden noktaları topla
        const allPoints: THREE.Vector3[] = [];
        let totalArea = 0;
        let combinedNormal = new THREE.Vector3();

        faceIds.forEach(faceId => {
            const face = brep.faces.get(faceId);
            if (!face) return;

            combinedNormal.add(face.normal);
            totalArea += face.area;

            // Face noktalarını topla
            if (face.surface.type === 'freeform' && face.surface.triangles) {
                face.surface.triangles.forEach(tri => {
                    allPoints.push(tri.a, tri.b, tri.c);
                });
            }
        });

        if (allPoints.length < 3) return null;

        // Ortalama normal'i normalize et
        combinedNormal.normalize();

        // Tüm noktalar için improved plane fit
        const plane = this.fitPlane(allPoints);
        if (!plane) return null;

        // Ortalama normal ile plane normal'ın tutarlılığını kontrol et
        const dotProduct = Math.abs(combinedNormal.dot(plane.normal));
        if (dotProduct < 0.95) return null;

        // Merkezi ve boyutları hesapla
        const mergedFace: BrepFace = {
            id: `PLANE_MERGED_${faceIds[0]}`,
            surface: {
                type: 'plane',
                origin: plane.origin,
                normal: plane.normal,
                uAxis: this.computeUAxis(plane.normal),
                vAxis: this.computeVAxis(plane.normal)
            },
            loops: [],
            edges: [],
            normal: plane.normal,
            area: totalArea,
            centroid: plane.origin
        };

        // Edge'leri birleştir (komşu face'lerin edge'lerini birleştir)
        const mergedEdges = new Set<string>();
        faceIds.forEach(faceId => {
            const face = brep.faces.get(faceId);
            if (face) {
                face.edges.forEach(edgeId => mergedEdges.add(edgeId));
            }
        });
        mergedFace.edges = Array.from(mergedEdges);

        return mergedFace;
    }

    /**
     * Bir silindir grubunu tek silindir olarak birleştirir
     */
    private mergeCylinderGroup(brep: BrepBody, faceIds: string[]): BrepFace | null {
        if (faceIds.length === 0) return null;

        // Tüm face'lerden verileri topla
        let totalHeight = 0;
        let combinedAxis = new THREE.Vector3();
        const radii: number[] = [];

        faceIds.forEach(faceId => {
            const face = brep.faces.get(faceId);
            if (!face || face.surface.type !== 'cylinder') return;

            const cylinder = face.surface as any;
            combinedAxis.add(cylinder.axis);
            totalHeight += cylinder.height || 0;
            radii.push(cylinder.radius);
        });

        if (radii.length === 0) return null;

        // Ortalama ekseni normalize et
        combinedAxis.normalize();

        // Ortalama radius ve yükseklik
        const avgRadius = radii.reduce((a, b) => a + b, 0) / radii.length;
        const avgHeight = totalHeight / faceIds.length;

        // Center'ı hesapla (tüm yüzey merkezlerinin ortalaması)
        let center = new THREE.Vector3();
        faceIds.forEach(faceId => {
            const face = brep.faces.get(faceId);
            if (face && face.surface.type === 'cylinder') {
                const cylinder = face.surface as any;
                if (cylinder.center) {
                    center.add(cylinder.center);
                }
            }
        });
        center.divideScalar(faceIds.length);

        const mergedFace: BrepFace = {
            id: `CYL_MERGED_${faceIds[0]}`,
            surface: {
                type: 'cylinder',
                axis: combinedAxis,
                center: center,
                radius: avgRadius,
                height: avgHeight
            },
            loops: [],
            edges: [],
            normal: new THREE.Vector3(),
            area: 0, // Silindir için alan hesabı yapılabilir
            centroid: center
        };

        // Edge'leri birleştir
        const mergedEdges = new Set<string>();
        faceIds.forEach(faceId => {
            const face = brep.faces.get(faceId);
            if (face) {
                face.edges.forEach(edgeId => mergedEdges.add(edgeId));
            }
        });
        mergedFace.edges = Array.from(mergedEdges);

        return mergedFace;
    }

    /**
     * Grid-based hızlı komşuluk analizi
     */
    public analyzeTopologyFast(brep: BrepBody): {
        manifold: boolean,
        watertight: boolean,
        nonManifoldEdges: string[],
        boundaryEdges: string[],
        isolatedVertices: string[]
    } {
        const nonManifoldEdges: string[] = [];
        const boundaryEdges: string[] = [];
        const isolatedVertices: string[] = [];

        // Edge analiz
        brep.edges.forEach((edge, edgeId) => {
            if (edge.faces.length > 2) {
                nonManifoldEdges.push(edgeId);
            }
            if (edge.faces.length === 1) {
                boundaryEdges.push(edgeId);
            }
        });

        // Vertex analiz
        brep.vertices.forEach((vertex, vertexId) => {
            if (vertex.edges.length === 0) {
                isolatedVertices.push(vertexId);
            }
        });

        // Manifold kontrolü
        const manifold = nonManifoldEdges.length === 0;

        // Watertight kontrolü (boundary edge yoksa)
        const watertight = boundaryEdges.length === 0;

        return {
            manifold,
            watertight,
            nonManifoldEdges,
            boundaryEdges,
            isolatedVertices
        };
    }

    /**
     * Hacim hesaplama - optimize edilmiş
     */
    public calculateVolumeOptimized(brep: BrepBody): number {
        let volume = 0;

        // Sadece kapalı shell'lerde hacim hesapla
        brep.shells.forEach(shell => {
            if (shell.isClosed && shell.volume) {
                volume += shell.volume;
            }
        });

        // Direct solid'ler
        brep.solids.forEach(solid => {
            volume += solid.volume;
        });

        return volume;
    }

    /**
     * Yüzey alanı hesaplama
     */
    public calculateSurfaceArea(brep: BrepBody): number {
        let totalArea = 0;

        brep.faces.forEach(face => {
            totalArea += face.area;
        });

        return totalArea;
    }

    /**
     * Kırıkkırık yüzey hesabı
     */
    public calculateSharpEdges(brep: BrepBody, angleThreshold: number = 30): {
        sharpEdges: BrepEdge[];
        sharpEdgeCount: number;
        sharpEdgeRatio: number;
    } {
        const sharpEdges: BrepEdge[] = [];

        brep.edges.forEach(edge => {
            if (edge.faces.length === 2 && edge.angle > angleThreshold) {
                sharpEdges.push(edge);
            }
        });

        return {
            sharpEdges,
            sharpEdgeCount: sharpEdges.length,
            sharpEdgeRatio: brep.edges.size > 0 ? sharpEdges.length / brep.edges.size : 0
        };
    }

    /**
     * Merkezi nokta bulma
     */
    public findCenterOfMass(brep: BrepBody): THREE.Vector3 {
        if (brep.solids.size > 0) {
            // Solid'lerin merkezi
            const center = new THREE.Vector3();
            let totalVolume = 0;

            brep.solids.forEach(solid => {
                center.add(solid.centerOfMass.multiplyScalar(solid.volume));
                totalVolume += solid.volume;
            });

            if (totalVolume > 0) {
                return center.divideScalar(totalVolume);
            }
        }

        // Vertex'lerin ağırlık merkezi (fallback)
        const center = new THREE.Vector3();
        let count = 0;

        brep.vertices.forEach(vertex => {
            center.add(vertex.position);
            count++;
        });

        return count > 0 ? center.divideScalar(count) : new THREE.Vector3();
    }

    /**
     * Bounding box küpü
     */
    public getExpandedBoundingBox(brep: BrepBody, expansion: number = 0.01): THREE.Box3 {
        const box = brep.boundingBox.clone();
        box.expandByScalar(expansion);
        return box;
    }

    /**
     * Geometrik validasyon
     */
    public validateGeometry(brep: BrepBody): {
        valid: boolean;
        errors: string[];
        warnings: string[];
    } {
        const errors: string[] = [];
        const warnings: string[] = [];

        // Boş kontrolleri
        if (brep.vertices.size === 0) {
            errors.push('No vertices found');
        }
        if (brep.faces.size === 0) {
            errors.push('No faces found');
        }

        // Topoloji kontrolleri
        const topology = this.analyzeTopologyFast(brep);
        if (!topology.manifold) {
            warnings.push(`Non-manifold mesh: ${topology.nonManifoldEdges.length} edges have more than 2 faces`);
        }
        if (!topology.watertight) {
            warnings.push(`Not watertight: ${topology.boundaryEdges.length} boundary edges found`);
        }

        // Geometrik kontrolleri
        let zeroLengthEdges = 0;
        let zeroAreaFaces = 0;
        let degenerateFaces = 0;

        brep.edges.forEach(edge => {
            if (edge.length < this.tolerance) {
                zeroLengthEdges++;
            }
        });

        brep.faces.forEach(face => {
            if (face.area < this.tolerance * this.tolerance) {
                zeroAreaFaces++;
            }

            if (face.area < this.tolerance * this.tolerance * 100) {
                degenerateFaces++;
            }
        });

        if (zeroLengthEdges > 0) {
            warnings.push(`${zeroLengthEdges} zero-length edges found`);
        }
        if (zeroAreaFaces > 0) {
            warnings.push(`${zeroAreaFaces} zero-area faces found`);
        }
        if (degenerateFaces > 0) {
            warnings.push(`${degenerateFaces} degenerate faces found`);
        }

        const valid = errors.length === 0;

        return {
            valid,
            errors,
            warnings
        };
    }

    /**
     * BREP'i ihraç et (debug için)
     */
    public exportToJSON(brep: BrepBody, pretty: boolean = false): string {
        const data = {
            stats: brep.stats,
            vertices: Array.from(brep.vertices.values()).map(v => ({
                id: v.id,
                position: v.position.toArray(),
                edges: v.edges
            })),
            edges: Array.from(brep.edges.values()).map(e => ({
                id: e.id,
                startVertex: e.startVertex,
                endVertex: e.endVertex,
                length: e.length,
                faces: e.faces,
                convex: e.convex,
                angle: e.angle
            })),
            faces: Array.from(brep.faces.values()).map(f => ({
                id: f.id,
                surface: {
                    type: f.surface.type,
                    // Surface tipine özel veriler
                    ...(f.surface.type === 'plane' && {
                        origin: (f.surface as any).origin?.toArray(),
                        normal: (f.surface as any).normal?.toArray()
                    }),
                    ...(f.surface.type === 'cylinder' && {
                        axis: (f.surface as any).axis?.toArray(),
                        center: (f.surface as any).center?.toArray(),
                        radius: (f.surface as any).radius,
                        height: (f.surface as any).height
                    }),
                    ...(f.surface.type === 'sphere' && {
                        center: (f.surface as any).center?.toArray(),
                        radius: (f.surface as any).radius
                    }),
                    ...(f.surface.type === 'cone' && {
                        apex: (f.surface as any).apex?.toArray(),
                        axis: (f.surface as any).axis?.toArray(),
                        radius: (f.surface as any).radius,
                        angle: (f.surface as any).angle
                    }),
                    ...(f.surface.type === 'torus' && {
                        center: (f.surface as any).center?.toArray(),
                        axis: (f.surface as any).axis?.toArray(),
                        majorRadius: (f.surface as any).majorRadius,
                        minorRadius: (f.surface as any).minorRadius
                    })
                },
                area: f.area,
                centroid: f.centroid.toArray(),
                normal: f.normal.toArray()
            }))
        };

        return pretty ? JSON.stringify(data, null, 2) : JSON.stringify(data);
    }

    /**
     * BREP yığını ezile
     */
    public importFromJSON(jsonString: string): BrepBody | null {
        try {
            const data = JSON.parse(jsonString);
            const brep: BrepBody = {
                vertices: new Map(),
                edges: new Map(),
                loops: new Map(),
                faces: new Map(),
                shells: new Map(),
                solids: new Map(),
                tolerance: data.stats?.tolerance || this.tolerance,
                boundingBox: new THREE.Box3(),
                stats: data.stats
            };

            // Vertex'leri geri yükle
            data.vertices?.forEach((v: any) => {
                brep.vertices.set(v.id, {
                    id: v.id,
                    position: new THREE.Vector3(...v.position),
                    tolerance: this.tolerance,
                    edges: v.edges
                });
            });

            // Edge'leri geri yükle
            data.edges?.forEach((e: any) => {
                const start = brep.vertices.get(e.startVertex);
                const end = brep.vertices.get(e.endVertex);

                if (start && end) {
                    const curve: BrepCurve = {
                        type: 'line',
                        start: start.position,
                        end: end.position,
                        length: e.length
                    };

                    brep.edges.set(e.id, {
                        id: e.id,
                        startVertex: e.startVertex,
                        endVertex: e.endVertex,
                        curve,
                        faces: e.faces || [],
                        convex: e.convex,
                        angle: e.angle || 0,
                        length: e.length
                    });
                }
            });

            // Face'leri geri yükle
            data.faces?.forEach((f: any) => {
                const surface: BrepSurface = f.surface;

                // Transform array'ları back to THREE objects
                if (surface.type === 'plane' && surface.origin) {
                    (surface as any).origin = new THREE.Vector3(...surface.origin);
                    (surface as any).normal = new THREE.Vector3(...surface.normal);
                    (surface as any).uAxis = new THREE.Vector3();
                    (surface as any).vAxis = new THREE.Vector3();
                } else if (surface.type === 'cylinder' && surface.axis) {
                    (surface as any).axis = new THREE.Vector3(...surface.axis);
                    (surface as any).center = new THREE.Vector3(...surface.center);
                } else if (surface.type === 'sphere' && surface.center) {
                    (surface as any).center = new THREE.Vector3(...surface.center);
                }

                brep.faces.set(f.id, {
                    id: f.id,
                    surface,
                    loops: f.loops || [],
                    edges: f.edges || [],
                    normal: f.normal ? new THREE.Vector3(...f.normal) : new THREE.Vector3(),
                    area: f.area || 0,
                    centroid: f.centroid ? new THREE.Vector3(...f.centroid) : new THREE.Vector3(),
                    color: f.color ? new THREE.Color(f.color) : undefined
                });
            });

            return brep;
        } catch (error) {
            return null;
        }
    }

    /**
     * İstatistik raporu oluştur
     */
    public generateStatisticsReport(brep: BrepBody): {
        report: string;
        summary: {
            entityCounts: Record<string, number>;
            geometryStats: Record<string, number>;
            topologyInfo: Record<string, boolean>;
        };
    } {
            const topology = this.analyzeTopology(brep);

            const summary = {
                entityCounts: {
                    vertices: brep.stats.vertexCount,
                    edges: brep.stats.edgeCount,
                    faces: brep.stats.faceCount,
                    shells: brep.stats.shellCount,
                    solids: brep.stats.solidCount
                },
                geometryStats: {
                    totalVolume: brep.stats.totalVolume,
                    totalArea: brep.stats.totalArea || 0
                },
                topologyInfo: {
                    manifold: topology.manifold,
                    watertight: topology.watertight,
                    orientable: topology.orientable
                }
            };

            const report = `
=== FikoBrep Analysis Report ===
Entity Counts:
  Vertices: ${summary.entityCounts.vertices}
  Edges: ${summary.entityCounts.edges}
  Faces: ${summary.entityCounts.faces}
  Shells: ${summary.entityCounts.shells}
  Solids: ${summary.entityCounts.solids}

Geometry Stats:
  Total Volume: ${summary.geometryStats.totalVolume.toFixed(6)}
  Total Surface Area: ${summary.geometryStats.totalArea.toFixed(6)}

Topology:
  Manifold: ${summary.topologyInfo.manifold ? 'Yes' : 'No'}
  Watertight: ${summary.topologyInfo.watertight ? 'Yes' : 'No'}
  Orientable: ${summary.topologyInfo.orientable ? 'Yes' : 'No'}

Euler Characteristic: ${topology.eulerCharacteristic}
Genus: ${topology.genus}

Surface Types:
  Planes: ${summary.entityCounts.faces - summary.geometryStats.totalArea} / summary.entityCounts.faces}
  Cylinders: ${this.countSurfaceType(brep, 'cylinder')}
  Spheres: ${this.countSurfaceType(brep, 'sphere')}
  Cones: ${this.countSurfaceType(brep, 'cone')}
  Tori: ${this.countSurfaceType(brep, 'torus')}
  Freeform: ${this.countSurfaceType(brep, 'freeform')}
            `.trim();

            return { report, summary };
        }

    /**
     * Surface tipini say
     */
    private countSurfaceType(brep: BrepBody, type: BrepSurface['type']): number {
        let count = 0;
        brep.faces.forEach(face => {
            if (face.surface.type === type) {
                count++;
            }
        });
        return count;
    }

    private computeUAxis(normal: THREE.Vector3): THREE.Vector3 {
        const arbitrary = Math.abs(normal.x) < 0.9
            ? new THREE.Vector3(1, 0, 0)
            : new THREE.Vector3(0, 1, 0);
        return new THREE.Vector3().crossVectors(normal, arbitrary).normalize();
    }

    private computeVAxis(normal: THREE.Vector3): THREE.Vector3 {
        const u = this.computeUAxis(normal);
        return new THREE.Vector3().crossVectors(normal, u).normalize();
    }

    // ========== TOPOLOGY ANALYSIS ==========

    private analyzeTopology(brep: BrepBody): MeshAnalysisResult['topology'] {
        // Manifold kontrolü
        let manifold = true;
        brep.edges.forEach(edge => {
            if (edge.faces.length > 2) manifold = false;
        });

        // Watertight kontrolü
        let watertight = true;
        brep.edges.forEach(edge => {
            if (edge.faces.length !== 2) watertight = false;
        });

        // Euler karakteristiği
        const V = brep.vertices.size;
        const E = brep.edges.size;
        const F = brep.faces.size;
        const eulerCharacteristic = V - E + F;

        // Genus
        const genus = watertight ? Math.max(0, 1 - Math.floor(eulerCharacteristic / 2)) : 0;

        return {
            manifold,
            watertight,
            orientable: true,
            eulerCharacteristic,
            genus
        };
    }

    private analyzeGeometry(brep: BrepBody): MeshAnalysisResult['geometry'] {
        const convexEdges: BrepEdge[] = [];
        const concaveEdges: BrepEdge[] = [];
        const sharpEdges: BrepEdge[] = [];
        const smoothEdges: BrepEdge[] = [];

        brep.edges.forEach(edge => {
            if (edge.faces.length === 2) {
                if (edge.angle > 30) {
                    sharpEdges.push(edge);
                } else if (edge.angle < 10) {
                    smoothEdges.push(edge);
                }

                if (edge.convex) {
                    convexEdges.push(edge);
                } else {
                    concaveEdges.push(edge);
                }
            }
        });

        return { convexEdges, concaveEdges, sharpEdges, smoothEdges };
    }

    private classifyFeatures(brep: BrepBody): MeshAnalysisResult['features'] {
        const planes: BrepFace[] = [];
        const cylinders: BrepFace[] = [];
        const spheres: BrepFace[] = [];
        const cones: BrepFace[] = [];
        const tori: BrepFace[] = [];
        const freeform: BrepFace[] = [];

        brep.faces.forEach(face => {
            switch (face.surface.type) {
                case 'plane': planes.push(face); break;
                case 'cylinder': cylinders.push(face); break;
                case 'sphere': spheres.push(face); break;
                case 'cone': cones.push(face); break;
                case 'torus': tori.push(face); break;
                default: freeform.push(face); break;
            }
        });

        return { planes, cylinders, spheres, cones, tori, freeform };
    }

    // ========== HELPER METHODS ==========

    private edgeHash(v0: string, v1: string): string {
        return v0 < v1 ? `${v0}-${v1}` : `${v1}-${v0}`;
    }
}
